import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Cropper from 'react-easy-crop';
import { useTranslation } from 'react-i18next';

import OverlaySelector from './OverlaySelector';
import ExportSettings from './ExportSettings';
import LanguageToggle from './LanguageToggle';
import { createCompositeBlob, readFileAsDataUrl, triggerDownload } from '../utils/canvas';
import { OVERLAYS } from '../utils/overlays';
import {
  getBrowserType,
  isIOSDevice,
  getInstallInstructions,
  supportsBeforeInstallPrompt,
} from '../utils/browserDetection';

import 'react-easy-crop/react-easy-crop.css';

const INNER_CIRCLE_RATIO = 450.0364 / 500;

function isStandaloneMode() {
  if (typeof window === 'undefined') {
    return false;
  }

  const standaloneDisplay = window.matchMedia && window.matchMedia('(display-mode: standalone)').matches;
  const navigatorStandalone = typeof navigator !== 'undefined' && navigator.standalone === true;

  return standaloneDisplay || navigatorStandalone;
}

function getStoredInstallState() {
  if (typeof window === 'undefined') {
    return false;
  }

  return window.localStorage.getItem('ardosz-pwa-installed') === 'true';
}

export default function PhotoOverlay() {
  const { t } = useTranslation();
  const cropFrameRef = useRef(null);

  const [imageSrc, setImageSrc] = useState('');
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [cropPixels, setCropPixels] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [installMessage, setInstallMessage] = useState('');
  const [cropSize, setCropSize] = useState(null);
  const [selectedOverlayId, setSelectedOverlayId] = useState(OVERLAYS[0].id);
  const [exportOptions, setExportOptions] = useState({ width: 1080, height: 1080, quality: 1 });
  const [deferredInstallPrompt, setDeferredInstallPrompt] = useState(null);
  const [isInstalled, setIsInstalled] = useState(isStandaloneMode() || getStoredInstallState());
  const [browserType] = useState(() => getBrowserType());
  const browserSupportsInstallPrompt = useMemo(() => supportsBeforeInstallPrompt(), []);

  const canShowInstallButton = !isInstalled
    && (!browserSupportsInstallPrompt || Boolean(deferredInstallPrompt));

  const activeOverlay = useMemo(() => {
    return OVERLAYS.find((overlay) => overlay.id === selectedOverlayId) || OVERLAYS[0];
  }, [selectedOverlayId]);

  const onFileChange = useCallback(async (event) => {
    const [file] = event.target.files || [];
    if (!file) {
      return;
    }

    try {
      setErrorMessage('');
      const dataUrl = await readFileAsDataUrl(file);
      setImageSrc(dataUrl);
      setZoom(1);
      setCrop({ x: 0, y: 0 });
      setCropPixels(null);
    } catch (error) {
      setErrorMessage(t('errorLoad'));
    }
  }, [t]);

  const onCropComplete = useCallback((_, croppedAreaPixels) => {
    setCropPixels(croppedAreaPixels);
  }, []);

  useEffect(() => {
    const frameNode = cropFrameRef.current;
    if (!frameNode) {
      return undefined;
    }

    const updateCropSize = () => {
      const { width, height } = frameNode.getBoundingClientRect();
      const diameter = Math.min(width, height) * INNER_CIRCLE_RATIO;
      setCropSize({ width: diameter, height: diameter });
    };

    updateCropSize();

    const resizeObserver = new ResizeObserver(() => {
      updateCropSize();
    });

    resizeObserver.observe(frameNode);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  useEffect(() => {
    const onBeforeInstallPrompt = (event) => {
      event.preventDefault();
      setDeferredInstallPrompt(event);
      setInstallMessage('');
    };

    const onAppInstalled = () => {
      setIsInstalled(true);
      setDeferredInstallPrompt(null);
      setInstallMessage('');
      window.localStorage.setItem('ardosz-pwa-installed', 'true');
    };

    window.addEventListener('beforeinstallprompt', onBeforeInstallPrompt);
    window.addEventListener('appinstalled', onAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstallPrompt);
      window.removeEventListener('appinstalled', onAppInstalled);
    };
  }, []);

  // Detect when app is uninstalled by checking if we're no longer in standalone mode
  // but previously marked as installed
  useEffect(() => {
    const checkIfUninstalled = () => {
      const wasInstalled = getStoredInstallState();
      const stillStandalone = isStandaloneMode();

      if (wasInstalled && !stillStandalone) {
        // App was installed but is no longer in standalone mode = app was uninstalled
        setIsInstalled(false);
        window.localStorage.removeItem('ardosz-pwa-installed');
        setInstallMessage(t('installRecovered'));
      }
    };

    // Check immediately on mount
    checkIfUninstalled();

    // Check periodically (every 2 seconds) when not in standalone mode
    // to detect if app was uninstalled from another browser window
    const interval = !isStandaloneMode() ? setInterval(checkIfUninstalled, 2000) : null;

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [t]);

  const handleDownload = useCallback(async () => {
    if (!imageSrc || !cropPixels) {
      return;
    }

    try {
      setIsProcessing(true);
      setErrorMessage('');
      const blob = await createCompositeBlob({
        imageSrc,
        cropPixels,
        overlayUrl: activeOverlay.url,
        exportOptions,
      });
      await triggerDownload(blob, 'photo-overlay-export.png');
    } catch (error) {
      setErrorMessage(t('errorExport'));
    } finally {
      setIsProcessing(false);
    }
  }, [activeOverlay.url, cropPixels, exportOptions, imageSrc, t]);

  const handleInstall = useCallback(async () => {
    setInstallMessage('');

    const isIOS = isIOSDevice();
    const installType = getInstallInstructions(browserType, isIOS);

    if (installType === 'prompt' && deferredInstallPrompt) {
      // Chrome/Edge: use beforeinstallprompt
      deferredInstallPrompt.prompt();
      const choiceResult = await deferredInstallPrompt.userChoice;
      setDeferredInstallPrompt(null);

      if (choiceResult?.outcome !== 'accepted') {
        setInstallMessage(t('installCancelled'));
      }
      return;
    }

    if (installType === 'prompt') {
      setInstallMessage(t('installUnavailable'));
      return;
    }

    if (installType === 'ios') {
      setInstallMessage(t('installIosHint'));
      return;
    }

    if (installType === 'safari') {
      setInstallMessage(t('installSafariHint'));
      return;
    }

    if (installType === 'firefox') {
      setInstallMessage(t('installFirefoxHint'));
      return;
    }

    if (installType === 'opera') {
      setInstallMessage(t('installOperaHint'));
      return;
    }

    setInstallMessage(t('installGenericHint'));
  }, [deferredInstallPrompt, browserType, t]);

  return (
    <section className="ardosz-overlay-shell">
      <header className="ardosz-header">
        <div>
          <h1>{t('title')}</h1>
          <p>{t('description')}</p>
        </div>
        <LanguageToggle />
      </header>

      <div className="ardosz-overlay-toolbar">
        <label className="ardosz-button ardosz-button-upload">
          {t('uploadPhoto')}
          <input type="file" accept="image/*" onChange={onFileChange} />
        </label>

        <button
          type="button"
          className="ardosz-button"
          onClick={handleDownload}
          disabled={!imageSrc || !cropPixels || isProcessing}
        >
          {isProcessing ? t('exporting') : t('downloadPng')}
        </button>

        {canShowInstallButton ? (
          <button
            type="button"
            className="ardosz-button"
            onClick={handleInstall}
          >
            {t('installApp')}
          </button>
        ) : null}
      </div>

      <OverlaySelector
        overlays={OVERLAYS}
        selectedOverlayId={selectedOverlayId}
        onChange={setSelectedOverlayId}
      />

      <ExportSettings options={exportOptions} onChange={setExportOptions} />

      {imageSrc ? (
        <>
          <div className="ardosz-crop-frame" ref={cropFrameRef}>
            <div className="ardosz-cropper-layer">
              <Cropper
                image={imageSrc}
                crop={crop}
                zoom={zoom}
                aspect={1}
                cropSize={cropSize || undefined}
                cropShape="round"
                objectFit="cover"
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={onCropComplete}
                showGrid={false}
              />
            </div>
            <img src={activeOverlay.url} alt={t('overlayPreview')} className="ardosz-overlay-preview" />
          </div>

          <div className="ardosz-control-group ardosz-zoom-control">
            <label htmlFor="ardosz-zoom-range">{t('scale')}</label>
            <input
              id="ardosz-zoom-range"
              type="range"
              min="1"
              max="3"
              step="0.01"
              value={zoom}
              onChange={(event) => setZoom(Number(event.target.value))}
            />
          </div>
        </>
      ) : (
        <p className="ardosz-hint">{t('hint')}</p>
      )}

      {errorMessage ? <p className="ardosz-error">{errorMessage}</p> : null}
      {installMessage ? <p className="ardosz-install-message">{installMessage}</p> : null}
      <p className="ardosz-privacy-note">{t('privacyNotice')}</p>
    </section>
  );
}
